import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { Dialog, Box, Paper, Grid, Typography, Tooltip, IconButton } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DrawIcon from "@mui/icons-material/Draw";

import TabGridAndFormLayout from "../../../Components/ProForms/ProFormsLayout/TabGridAndFormLayout";
import ProGrid from "../../../Components/ProGrid/v2";
import ProButton from "../../../Components/ProButton";
import ReviewDocument from "./ReviewDocument";

import usePageState from "../../../../utils/customHooks/usePageState";
import usePermission from "../../../../utils/customHooks/usePermission";
import useColumn from "../../../../utils/customHooks/useColumns";
import { dateColumnDef } from "../../../Components/ProGrid/components/FormatCell";

import { employeesDocumentsPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import { userSelector } from "../../../store/features/auth/authSlice";

import { PANDADOC_API_KEY, ETA671_TEMPLATE_UUID, STAUTES, PANDADOC_API_URI } from "../../../../utils/constant";
import { errorToast } from "../../../../utils/toastHelper";
import { getApi, postApi } from "../../../../utils/services";
import { employeeDocumentsFormName, downloadFile } from "../../../../utils/formHelper";
import { v4 as uuidV4 } from "uuid";

import { subModPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import { R_ALL_DOCUMENTS, R_DOCUMENTS_671_FORM } from "../../../../utils/permissionReferenceConfig";

import {
  ETA_FORM671_RECORDS,
  ETA_FORM_STATUS,
  ADD_EMPLOYEE_FORM,
  SEND_NOTIFICATION_FOR_DOCUMENT_SIGNATURE,
  GET_ALL_DOCUMENTS
} from "../../../../utils/services/apiPath";

import "./EmployeeDetail.scss";

import { MappingForETA671Form } from "../EmployeeData";

export default function ESignature() {
  const { id } = useParams();
  const user = useSelector(userSelector);

  const {
    currentPage,
    pageSize,
    data: documentData,
    rowCount,
    status,
    sortModel,
    searchString,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString
  } = usePageState();

  const { canAdd, canEdit, canExport: canExport671 } = useSelector(subModPermissionSelector(R_ALL_DOCUMENTS, R_DOCUMENTS_671_FORM));

  const [documentId, setDocumentId] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [formName, setFormName] = useState("");
  const [aPIKey, setAPIKey] = useState("");

  const [apprenticeEmail, setApprenticeEmail] = useState("");
  const [apprenticeFirstName, setApprenticeFirstName] = useState("");
  const [apprenticeLastName, setApprenticeLastName] = useState("");

  const [open, setOpen] = useState(false);
  const [openSignDocument, setOpenSignDocument] = useState(false);
  const [isDisableCreate671Btn, setIsDisableCreate671Btn] = useState(false);
  const [buttonCreate671, setButtonCreate671] = useState("Create 671 Form");
  const [sendDocumentStatus, setSendDocumentStatus] = useState(false);
  const [isDocumentSent, setIsDocumentSent] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [openMessage, setOpenMessage] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);

  const { [employeeDocumentsFormName]: employees } = usePermission(employeesDocumentsPermissionSelector, employeeDocumentsFormName);

  const columns = useColumn([
    {
      field: "formName",
      headerName: "Form Name"
    },
    dateColumnDef({
      field: "createdDate",
      headerName: "Created Date"
    }),
    {
      field: "status",
      headerName: "ESigned Status"
    },
    {
      field: "rapidstatus",
      headerName: "Rapid Status"
    },
    {
      field: "firstSignerSharedLink",
      headerName: "Actions",
      disableExport: !canExport671,
      renderCell: params => {
        const is671Form = params.row.formName?.toLowerCase().includes("671");
        const isExportDisabled = !canExport671;
        const isSignDisabled = !canEdit;

        return (
          <>
            {params.row.status === "Completed" ? (
              <Tooltip title={"Download Document"} arrow>
                  <IconButton color="primary" onClick={() => handleDownloadDocument(params.row.documentUrl)} disabled={isExportDisabled}
                    sx={{opacity: isExportDisabled ? 0 : 1, cursor: isExportDisabled ? "not-allowed" : "pointer"}}>
                    <FileDownloadIcon />
                  </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={"Sign Document"} arrow>
                <IconButton color="primary" onClick={() => handleSignDocument(params.row.secondSignerSharedLink)} disabled={isSignDisabled} sx={{opacity: isSignDisabled ? 0 : 1, cursor: isSignDisabled ? "not-allowed" : "pointer"}}>
                  <DrawIcon />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      }
    }
    
  ]);

  const getAllDocuments = useCallback(async () => {
    setStatus(STAUTES.LOADING);
    const payload = {
      pageIndex: searchString ? 1 : currentPage + 1,
      pageSize: pageSize,
      searchString: searchString,
      orderByAscending: true,
      orderCol: "formName",
      ...sortModel
    };
    const { data, totalRecords, error } = await postApi(GET_ALL_DOCUMENTS, payload, { params: { employeeId: id } });
    setStatus(STAUTES.IDLE);
    if (error) return errorToast(error);
    setPageState(prevPageInfo => ({ ...prevPageInfo, data: data || [], rowCount: totalRecords }));
  }, [currentPage, pageSize, sortModel, searchString, setPageState, setStatus]);

  const handleSignDocument = formLink => {
    setSessionId(formLink);
    setOpenSignDocument(true);
  };

  const handleClose = () => {
    setOpen(false);
    getAllDocuments();
  };

  const onClose = () => {
    setOpenSignDocument(false);
  };

  const handleCloseMessage = () => {
    setOpenMessage(false);
  };

  const handleDownloadDocument = documentUrl => {
    downloadFile(documentUrl?.path);
  };

  const createMapping = (matchValues, mapObj) => {
    const mapValue = {};
    const matchArray = matchValues?.split(",") || [];
    for (const chkKey in mapObj) {
      mapValue[chkKey] = matchArray.includes(mapObj[chkKey]) ? mapObj[chkKey] : null;
    }
    return mapValue;
  };

  const convertToPDFFormat = originalObject => {
    const transformedObject = { ...originalObject };

    const dynamicMappings = {
      ethnicity: "ethnicityType",
      sex: "employeeSex",
      race: "raceType",
      veteranStatus: "veteranStatusType",
      educationLevel: "educationLevelType",
      apprenticeEmploymentStatus: "employeeStatus",
      occupationType: "occupationTypeValue",
      relatedInstructionHours: "relatedInstructionProvidedHours",
      wageRate: "wageRateType"
    };

    for (const key in MappingForETA671Form) {
      if (dynamicMappings[key]) {
        transformedObject[key] = createMapping(originalObject?.[dynamicMappings[key]], MappingForETA671Form[key]);
      } else if (key === "preApprenticeShipCompletion" || key === "interimCredentialsOffered") {
        transformedObject[key] = MappingForETA671Form[key];
      } else if (key === "isWagePaid") {
        transformedObject[key] = {
          chkIsWagePaidYes: originalObject?.isWageRatePaid ? "Y" : null,
          chkIsWagePaidNo: !originalObject?.isWageRatePaid ? "Y" : null
        };
      }
    }
    return transformedObject;
  };

  const convertToNewFormat = (originalObject, parentKey = "") => {
    const transformedObject = {};
    for (const key in originalObject) {
      const newKey = key.charAt(0).toUpperCase() + key.slice(1);
      const isChkOrRdKey = newKey.includes("Chk") || newKey.includes("Rd");
      if (Array.isArray(originalObject[key])) {
        transformedObject[newKey] = originalObject[key].map(item => {
          if (typeof item === "object" && item !== null) {
            return convertToNewFormat(item, newKey);
          } else {
            return { value: isChkOrRdKey ? item !== null && item !== "" : item };
          }
        });
      } else if (typeof originalObject[key] === "object" && originalObject[key] !== null) {
        Object.assign(transformedObject, convertToNewFormat(originalObject[key], newKey));
      } else {
        transformedObject[newKey] = { value: isChkOrRdKey ? originalObject[key] !== null && originalObject[key] !== "" : originalObject[key] };
      }
    }
    return transformedObject;
  };

  const GetETA671Form = async () => {
    setButtonCreate671("Loading...");
    let response = await getApi(`${ETA_FORM671_RECORDS}/${id}`);
    if (response) {
      const convertedObject = convertToNewFormat(convertToPDFFormat(response));
      const wageRateList = response.wageRateList;
      if (wageRateList != null) {
        for (let i = 1; i <= wageRateList.length; i++) {
          convertedObject["Duration" + i] = { value: wageRateList[i - 1].startHours + "-" + wageRateList[i - 1].endHours };
          convertedObject["WageRate" + i] = { value: wageRateList[i - 1].wageRate };
        }
      }
      delete convertedObject["WageRateList"];
      uploadDocumentFromURL(convertedObject, id, response);
    }
  };

  const getFormStatus = async () => {
    setStatus(STAUTES.LOADING);
    const { isFormStatusCompleted } = await getApi(`${ETA_FORM_STATUS}?employeeGuid=${id}`);
    setIsDisableCreate671Btn(!isFormStatusCompleted);
    setStatus(STAUTES.IDLE);
  };

  useEffect(() => {
    getAllDocuments();
  }, [getAllDocuments]);

  useEffect(() => {
    getFormStatus();
  }, []);

  const uploadDocumentFromURL = async (docData, key, dataPayload) => {
    try {
      const { firstName, lastName, email } = user;
      const apiKey = PANDADOC_API_KEY;
      const formName = "ETA Form Document";
      const templateUUId = ETA671_TEMPLATE_UUID;
      const uploadResponse = await fetch(`${PANDADOC_API_URI}/public/v1/documents`, {
        method: "POST",
        headers: {
          Authorization: `API-Key ${apiKey}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: formName,
          template_uuid: templateUUId,
          recipients: [
            {
              email: dataPayload.emailAddress,
              role: "apprentice",
              first_name: dataPayload.firstName,
              last_name: dataPayload.lastName,
              signing_order: 1
            },
            {
              email: email,
              role: "proscoreadmin",
              first_name: firstName,
              last_name: lastName,
              signing_order: 2
            }
          ],
          fields: docData,
          parse_form_fields: true
        })
      });
      if (uploadResponse.ok) {
        const resp = await uploadResponse.json();
        setApprenticeEmail(dataPayload.emailAddress);
        setApprenticeFirstName(dataPayload.firstName);
        setApprenticeLastName(dataPayload.lastName);
        setDocumentId(resp.id);
        setFormName(resp.name);
        setAPIKey(apiKey);
        getDocument(apiKey, resp.id, key, dataPayload.emailAddress);
        setTimeout(() => {
          addCC(apiKey, resp.id);
        }, 2000);
      } else {
        const errorResponse = await uploadResponse.json();
        throw new Error(`Failed to upload document: ${errorResponse.error}`);
      }
    } catch (error) {
      console.error("Error:", error.message);
    }
  };

  const addCC = async (apiKey, documentId) => {
    const options = {
      method: "POST",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
        Authorization: `API-Key ${apiKey}`
      },
      body: JSON.stringify({ kind: "contact", id: "pFqUHp3Xw2DH5nypF8P2RK" })
    };

    fetch(`${PANDADOC_API_URI}/public/v1/documents/${documentId}/recipients`, options)
      .then(res => res.json())
      .then(res => console.log(res))
      .catch(err => console.error(err));
  };

  const getDocument = async (apiKey, documentId, employeeGuid, apprenticeEmail) => {
    const obj = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `API-Key ${apiKey}`
      }
    };
    await fetch(`${PANDADOC_API_URI}/public/v1/documents/${documentId}`, obj)
      .then(response => response.json())
      .then(response => {
        if (response.status == "document.draft") {
          sendDocument(apiKey, documentId, employeeGuid, apprenticeEmail);
        } else {
          getDocument(apiKey, documentId, employeeGuid, apprenticeEmail);
        }
      });
  };

  const sendDocument = async (apiKey, documentId, employeeGuid, apprenticeEmail) => {
    const obj = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `API-Key ${apiKey}`
      },
      body: JSON.stringify({
        silent: true,
        to: apprenticeEmail,
        subject: "Document for Signature",
        message: "Please review and sign the document."
      })
    };

    try {
      const response = await fetch(`${PANDADOC_API_URI}/public/v1/documents/${documentId}/send`, obj);
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 400 && data?.detail?.code === "invalid_field_values") {
          setButtonCreate671("Create 671 Form");
          setOpenMessage(true);
          const invalidFields = data.detail.invalid_fields || [];
          const fieldErrors = invalidFields
            .map(field => {
              const fieldName = field.validation_settings?.type || "Unknown Field";
              const reason = field.reason || "Invalid value";
              return `Fieldname: ${fieldName}, Issue: ${reason}`;
            })
            .join(" | ");
          setModalMessage(`We were unable to generate the ETA-671 form. The server reported: ${fieldErrors}`);
        } else {
          setButtonCreate671("Create 671 Form");
          setOpenMessage(true);
          setModalMessage(`We were unable to generate the ETA-671 form.`);
        }
        return;
      }

      const { name, id, status, recipients } = data;
      setSessionId(recipients?.[1]?.shared_link);
      setOpen(true);
      addEmployeeForm(employeeGuid, name, id, status, recipients);
    } catch (error) {
      setButtonCreate671("Create 671 Form");
      setOpenMessage(true);
      setModalMessage(`We were unable to generate the ETA-671 form.`);
    }
  };

  const addEmployeeForm = async (employeeGuid, documentName, documentId, documentStatus, recipients) => {
    const payload = {
      employeeGuid: employeeGuid,
      documentId: documentId,
      formName: documentName,
      firstSignerSharedLink: recipients[0]?.shared_link,
      secondSignerSharedLink: recipients[1]?.shared_link,
      docStatus: documentStatus,
      isApprenticeSigned: false,
      isProscoreSigned: false
    };
    const { error } = await postApi(ADD_EMPLOYEE_FORM, payload);
    if (error) return errorToast(error);
    setButtonCreate671("Create 671 Form");
  };

  const sendNotificationForDocumentSignatures = async () => {
    setSendDocumentStatus(true);
    const payload = {
      email: apprenticeEmail,
      firstName: apprenticeFirstName,
      lastName: apprenticeLastName,
      documentId: documentId,
      formName: formName
    };
    const { error } = await postApi(SEND_NOTIFICATION_FOR_DOCUMENT_SIGNATURE, payload);
    if (error) return errorToast(error);
    setSendDocumentStatus(false);
    setIsDocumentSent(true);
    setOpenAlert(true);
  };

  return (
    <TabGridAndFormLayout showGrid={true} canView={employees.canView}>
      <ProGrid
        title="Documents"
        loading={status === STAUTES.LOADING}
        options={{
          getRowId: () => uuidV4(),
          rowCount: rowCount,
          paginationMode: "server",
          paginationModel: { pageSize: pageSize, page: currentPage },
          onPaginationModelChange: setPagination,
          sortingMode: "server",
          onSortModelChange: setSortModel
        }}
        columns={columns}
        hideExport={!employees.canExport}
        hideAddButton={true}
        rows={documentData}
        searchMode={!!searchString}
        searchModel={{ defaultValue: searchString, handleDebounce: setSearchString }}
        additionButtons={
          canAdd && (
            <ProButton tooltipText={isDisableCreate671Btn ? "You don't have SSN" : ""} disabled={isDisableCreate671Btn} onClick={GetETA671Form}>
              {buttonCreate671}
            </ProButton>
          )
        }
        onBeforeExport={(cols) => cols.filter(col => !col.hideFromExport)} 
      />
      {modalMessage && (
        <Dialog
          open={openMessage}
          onClose={handleCloseMessage}
          maxWidth="md"
          fullWidth
          keepMounted
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <Paper sx={{ py: 3, px: 2 }}>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>
                <Typography variant="h5" component="h5">
                  {modalMessage}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Dialog>
      )}

      <Dialog
        open={openSignDocument}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        keepMounted
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <Box sx={{ width: "100%" }}>
          <Paper sx={{ py: 3, px: 2 }}>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item alignItems="center">
                <Typography variant="h5" component="h5" gutterBottom textTransform="uppercase">
                  Sign Document
                </Typography>
              </Grid>
            </Grid>
          </Paper>
          <div>
            <iframe title="PDF Viewer" width="100%" height="600" src={sessionId}></iframe>
          </div>
        </Box>
      </Dialog>

      <ReviewDocument
        open={open}
        onClose={handleClose}
        documentId={documentId}
        apiKey={aPIKey}
        sessionId={sessionId}
        onClickSendDocument={sendNotificationForDocumentSignatures}
        SendDocumentStatus={sendDocumentStatus}
        IsDocumentSent={isDocumentSent}
        openAlert={openAlert}
      />
    </TabGridAndFormLayout>
  );
}